import { ArrowUp, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { AIBlock, ASK_GUV_EVENT, Button, Sheet, SheetContent, Textarea, type AskGuvDetail } from "@/design";
import { useProjectSlug } from "./context";

/**
 * "Ask Guv": context-aware AI help (docs/12 §1.3). Opened by askGuv() from any ExplainPanel or the sidebar.
 * The real assistant is not in the API contract yet; this panel shows the shape and labels the answer as AI.
 */
export function AskGuvPanel() {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<AskGuvDetail>({ question: "" });
  const [draft, setDraft] = useState("");
  const [asked, setAsked] = useState<string | null>(null);
  const project = useProjectSlug();

  useEffect(() => {
    const on = (e: Event) => {
      const d = (e as CustomEvent<AskGuvDetail>).detail;
      setDetail(d);
      setDraft(d.question);
      setAsked(d.question || null);
      setOpen(true);
    };
    window.addEventListener(ASK_GUV_EVENT, on);
    return () => window.removeEventListener(ASK_GUV_EVENT, on);
  }, []);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        size="md"
        title="Ask Guv"
        description={project ? `Answers use ${project}'s live status, logs and the docs.` : "Answers use your workspace's live status and the docs."}
        leading={<span className="flex size-8 items-center justify-center rounded-lg bg-ai-soft text-ai-text"><Sparkles className="size-4" /></span>}
        footer={
          <form
            className="flex w-full items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (draft.trim()) setAsked(draft.trim());
            }}
          >
            <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Why did my deploy fail? Can I afford another replica?" className="min-h-11 flex-1 resize-none" rows={1} />
            <Button type="submit" variant="primary" aria-label="Ask" className="size-11 px-0">
              <ArrowUp />
            </Button>
          </form>
        }
      >
        {asked ? (
          <div className="space-y-4">
            <div className="ml-auto max-w-[85%] rounded-xl rounded-br-sm bg-accent-soft px-3.5 py-2.5 text-base text-text">{asked}</div>
            <AIBlock>
              <p>
                I can see {project ? <strong>{project}</strong> : "your workspace"}
                {detail.context ? " and the details of what you were looking at" : ""}. In this preview build answers are placeholders; the
                assistant connects to Guv's AI operations service once its API lands.
              </p>
            </AIBlock>
          </div>
        ) : (
          <div className="space-y-2 text-base text-muted">
            <p>Ask anything about your apps in plain English. A few ideas:</p>
            <ul className="space-y-1.5">
              {["Why is my app slow today?", "What would it cost to add a staging environment?", "Is my database backed up?", "Explain my Safety grade."].map((q) => (
                <li key={q}>
                  <button type="button" className="text-left text-accent-text hover:underline" onClick={() => { setDraft(q); setAsked(q); }}>
                    {q}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
